import type { CreateLineItemParams, DeleteLineItemParams } from "@/interfaces/payloads/line-item";
import type { ChangeEvent } from "react";
import { useCallback, useState } from "react";
import type { Category } from "../Lines";
import type { IQuantityFormula } from "../Lines/util/engine-shared-utils";

export const useAddLineItem = (
  pageId: string,
  deleteThenCreateLineItem: (deleteParams: DeleteLineItemParams, createParams: CreateLineItemParams) => Promise<void>,
  createLineItem: (
    pageId: string,
    option: string,
    lineItemId: string,
    quantity?: number,
    measurement?: string,
    formula?: IQuantityFormula | null,
    orderIndex?: number,
  ) => Promise<void>,
) => {
  const [rawFormulaString, setRawFormulaString] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<string>();
  const [selectedLineItemID, setSelectedLineItemID] = useState<string>();
  const [orderIndex, setOrderIndex] = useState<number>();

  const reset = useCallback(() => {
    setRawFormulaString("");
    setSelectedOption(undefined);
  }, []);

  const onSaveLineItem = useCallback(
    async (deleteParams?: { tupleId: string; pageId: string; uniqueId: string; category?: Category }) => {
      if (!selectedOption || !selectedLineItemID) return;

      reset();

      const formula: IQuantityFormula | undefined = rawFormulaString
        ? {
            initialValue: 1,
            base: "ea",
            operator: "",
            modifierValue: 0,
            modifierSuffix: "",
            rawFormula: rawFormulaString,
            isMultiUnit: true,
          }
        : undefined;

      if (deleteParams) {
        await deleteThenCreateLineItem(deleteParams, {
          pageId,
          item: selectedOption,
          lineItemId: selectedLineItemID,
          quantity: formula ? 1 : undefined,
          measurement: formula ? "ea" : undefined,
          formula,
        });
      } else {
        await createLineItem(
          pageId,
          selectedOption,
          selectedLineItemID,
          formula ? 1 : undefined,
          formula ? "ea" : undefined,
          formula,
          orderIndex,
        );
      }
    },
    [
      reset,
      selectedOption,
      selectedLineItemID,
      deleteThenCreateLineItem,
      pageId,
      createLineItem,
      rawFormulaString,
      orderIndex,
    ],
  );

  const onChangeQuantity = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRawFormulaString(e.target.value);
  }, []);

  const onChangeLineItem = useCallback((value: string, id?: string) => {
    setSelectedOption(value);
    if (id) {
      setSelectedLineItemID(id);
    }
  }, []);

  return {
    onSaveLineItem,
    onChangeQuantity,
    onChangeLineItem,
    reset,
    setOrderIndex,
  };
};
